import { Component, Input, ElementRef, OnInit, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import get from 'lodash-es/get';

import { CampaignDrawerService } from '@finder/shared/services/campaign-drawer/campaign-drawer.service';
import { CampaignDrawerOpen, DEFAULT_DRAWER_STATE } from '@finder/shared/services/campaign-drawer/campaign-drawer.interface';
import { CampaignDrawerBase } from '@finder/shared/components/campaign-drawer-base/campaign-drawer-base';
import { ConfigService } from '@finder/core/config.service';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { HashTriggerableService } from '@finder/shared/features/hash-triggerable/hash-triggerable.service';
import { HexToRGBProvider, HEX_TO_RGB } from '@finder/shared/utils/dom/hexToRGB';
import { PickHexProvider, PICK_HEX } from '@finder/shared/utils/dom/pickHexColor';
import { UrlParamsService } from '@finder/shared/services/url-params/url-params.service';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { PictureComponent } from '../picture/picture.component';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';
import { ButtonComponent } from '../button/button.component';
import { CommonModule } from '@angular/common';
import { ScrollToDirective } from '@finder/shared/directives/scroll-to/scroll-to.directive';
import { FontIconComponent } from '../font-icon/font-icon.component';
import { AnchorComponent } from '../anchor/anchor.component';

const HASH_PREFIX = 'drawer-card';
const VIEW_MORE = 'viewMoreText';
const VIEW_LESS = 'viewLessText';
const BUTTON_ICONS_ENABLED: string = 'enabled';

@Component({
    selector: 'finder-campaign-drawer-card',
    templateUrl: './campaign-drawer-card.component.html',
    styleUrls: ['./campaign-drawer-card.component.scss'],
    standalone: true,
    imports: [
        PictureComponent,
        SafeHtmlPipe,
        ButtonComponent,
        CommonModule,
        ScrollToDirective,
        FontIconComponent,
        AnchorComponent,
    ]
})
export class CampaignDrawerCardComponent extends CampaignDrawerBase implements OnInit {
    @Input() cardData: any;
    @Input() groupId: number;
    @Input() cardIndex: number;
    @Input() showCaret: boolean;

    linkId: string;
    linkText: string;
    cardCssOrder: number;

    private hashPrefix = HASH_PREFIX;
    public buttonIconsEnabled: boolean;

    constructor(
        private elementRef: ElementRef,
        private finderAnalyticsService: FinderAnalyticsService,
        private windowRef: WindowRef,
        private viewport: ViewportDetectionService,
        private configService: ConfigService,
        private urlParamsService: UrlParamsService,
        protected drawerService: CampaignDrawerService,
        protected route: ActivatedRoute,
        protected hashTriggerableService: HashTriggerableService,
        @Inject(HEX_TO_RGB) private hexToRGB: HexToRGBProvider,
        @Inject(PICK_HEX) private pickHex: PickHexProvider,
    ) {
        super(hashTriggerableService, route, drawerService);
    }

    ngOnInit() {
        // Set default values
        this.elementId = this.getId();
        //Source id to be focused back on drawer close
        this.linkId = `${this.elementId}-link`;
        this.linkText = get(this.cardData, VIEW_MORE);

        // Validate if we have enabled button icons for link arrows or norgies
        this.buttonIconsEnabled = this.cardData?.buttonIcons === BUTTON_ICONS_ENABLED;

        // Call super
        super.ngOnInit();
    }

    override click(event) {
        // If the drawer card doesn't have a link just return.
        if (!this.linkText) {
            return;
        }
        super.click(event);

        // Act depending on the current state
        if (this.isExpanded) {
            // Broadcast that the drawer state has changed
            this.setOpen();

            // Update the hash
            this.hashTriggerableService.updateHash(this.elementId);
        } else {
            // Broadcast that the drawer state has changed
            this.drawerService.setOpen(DEFAULT_DRAWER_STATE);

            // Update the hash
            this.hashTriggerableService.removeHash();
        }

        const drawerCardState = this.isExpanded ? 'expanded' : 'collapsed';

        // Analytics tracking
        this.finderAnalyticsService.updateModelAndTrack({
            linkId: `${this.configService.getValue('siteId')}_` +
            `${this.urlParamsService.entityType}_drawer-card-${this.cardData.id}_` +
            `${drawerCardState}`
        });

        // Set caret style
        this.setCaretStyle();
    }

    protected expand() {
        // Make sure we don't keep calling this over and over
        if (this.isExpanded) {
            return;
        }

        // Set CSS order (for infographic cards)
        // This needs to happen before the deeplink element is expanded so it shows in the correct location
        this.setCssOrder();

        // If this is triggered from a page load broadcast to everyone
        if (this.hashTriggerableService.isTriggered) {
            this.setOpen();
        }

        super.expand();

        // Set the link text
        this.setLinkText();

        // Reset the hash trigger flag
        this.hashTriggerableService.disableTrigger();
    }

    protected collapse() {
        // Make sure we don't keep calling this over and over
        if (!this.isExpanded) {
            return;
        }

        super.collapse();

        // Set the link text
        this.setLinkText();
    }

    protected onOpen(data: CampaignDrawerOpen) {
        const isMatch = this.groupId === data.groupId && this.cardIndex === data.cardIndex;
        if (isMatch) {
            return;
        }

        // Collapse
        this.collapse();

        // Set link text
        this.setLinkText();
    }

    protected setOpen() {
        this.drawerService.setOpen({
            groupId: this.groupId,
            cardIndex: this.cardIndex,
            cardData: this.cardData,
            cardCssOrder: this.cardCssOrder,
            hasHash: this.hashTriggerableService.isTriggered,
            sourceId: this.linkId,
        });
    }

    private setCssOrder() {
        // Set the order of the card on the vm. This is used to order the desc panel for infographic
        // The default value is 0 and only infographic uses it
        this.cardCssOrder = Number(this.windowRef.nativeWindow
            .getComputedStyle(this.elementRef.nativeElement)
            .getPropertyValue('order'));

        /**
         * If the value here is -1 the new css hasn't been applied to the component. This will be fixed
         * once the page has completely loaded. We need this check to properly navigate to a deep link
         * in mobile. When the page loads the css that tells it where to go isn't applied yet.
         */
        if (this.cardCssOrder === -1) {
            const dataIndex = this.viewport.isMobile() ? 'mobileIndex' : 'desktopIndex';
            this.cardCssOrder = this.elementRef.nativeElement.dataset[dataIndex];
        }
    }

    private getId() {
        let result = get(this.cardData, 'id');

        // If there is a producer defined ID then use it but add the prefix we use for determining if
        // the hash is a drawer card hash or not (see above)
        // If there is no producer defined ID then generate one that uniquely defines this group/card
        if (result) {
            result = `${this.hashPrefix}-${result}`;
        } else {
            result = `${this.hashPrefix}-${this.groupId}-${this.cardIndex}`;
        }

        return result;
    }

    private setLinkText() {
        if (this.cardData.storyCardLink) {
            return;
        }
        // Set the text accordingly
        if (this.isExpanded) {
            this.linkText = get(this.cardData, VIEW_LESS, VIEW_LESS);
        } else {
            this.linkText = get(this.cardData, VIEW_MORE, VIEW_MORE);
        }
    }

    private setCaretStyle() {
        const el = this.elementRef.nativeElement;
        const doc = this.windowRef.nativeWindow.document;
        const body = doc.body;
        const computedStyles = getComputedStyle(body);
        const appBgColor = computedStyles.getPropertyValue('--app-content-background');
        if (!appBgColor) {
            // Remove any previously set caret color
            el.style.removeProperty('--drawer-caret-bg');

            return;
        }

        // Get colors
        const bgColor = this.hexToRGB(computedStyles.getPropertyValue('--finder-background-color'));
        const accentColor = this.hexToRGB(computedStyles.getPropertyValue('--finder-accent-color'));

        // Get element placement in the document
        const elRect = this.elementRef.nativeElement.getBoundingClientRect();
        const elBottom = this.windowRef.nativeWindow.pageYOffset - doc.documentElement.clientTop + elRect.top + elRect.height;
        const ratioBottom = elBottom / body.offsetHeight;

        // Determine hex color of bg at element placement
        const hexValue = this.pickHex(bgColor, accentColor, ratioBottom);

        // Set caret color
        el.style.setProperty('--drawer-caret-bg', hexValue);
    }
}
